import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';
import { toast } from "react-toastify";
import { useForm, Controller } from 'react-hook-form';
import { ConfigurationService } from '../../services/ConfigurationService';
import LoadingOverlay from '../loadingOverlay';


type TranscriberConfigFormData = {
    GptTuningStyle: number,
    CallRecorderSharePointUploadFolder: string,
    SharePointSiteName: string,
    ProofreadingSharePointFolderName: string,
    SummarizationSharePointFolderName : string
};

const TranscriberConfig = () => {
    const configForm = useForm<TranscriberConfigFormData>()
    const [loading, setLoading] = useState(false);

    const propertiesToRead = ["GptTuningStyle", "CallRecorderSharePointUploadFolder", "SharePointSiteName"
                            ,"ProofreadingFileFilter.SharePointFolderName","SummarizationFileFilter.SharePointFolderName"];
    useEffect(() => {
        setLoading(true);
        ConfigurationService.getConfiguration(propertiesToRead)
            .then((response: any) => {
                const configValues: TranscriberConfigFormData = {
                    GptTuningStyle: response.GptTuningStyle,
                    CallRecorderSharePointUploadFolder: response.CallRecorderSharePointUploadFolder,
                    SharePointSiteName: response.SharePointSiteName,
                    ProofreadingSharePointFolderName: response["ProofreadingFileFilter.SharePointFolderName"],
                    SummarizationSharePointFolderName: response["SummarizationFileFilter.SharePointFolderName"]
                };
                configForm.setValue('GptTuningStyle', configValues.GptTuningStyle);
                configForm.setValue('CallRecorderSharePointUploadFolder', configValues.CallRecorderSharePointUploadFolder);
                configForm.setValue('SharePointSiteName', configValues.SharePointSiteName);
                configForm.setValue('ProofreadingSharePointFolderName', configValues.ProofreadingSharePointFolderName);
                configForm.setValue('SummarizationSharePointFolderName', configValues.SummarizationSharePointFolderName);
                setLoading(false);
            })
            .catch((error: any) => {
                const errorMessage = error?.response?.data.ErrorMessage ? error?.response?.data.ErrorMessage : error?.response?.data
                toast.error(errorMessage);
                setLoading(false);
            });
    }, []);


    const onConfigSubmit = (transcriberConfig: TranscriberConfigFormData) => {
        const id = toast.loading("Please wait...");
        const apiPayload = {
            "ProofreadingFileFilter.SharePointFolderName": transcriberConfig.ProofreadingSharePointFolderName,
            "SummarizationFileFilter.SharePointFolderName": transcriberConfig.SummarizationSharePointFolderName,
            "SharePointSiteName": transcriberConfig.SharePointSiteName,
            "CallRecorderSharePointUploadFolder": transcriberConfig.CallRecorderSharePointUploadFolder,
            "GptTuningStyle": transcriberConfig.GptTuningStyle
        };
        
        ConfigurationService.saveConfiguration(apiPayload)
            .then((response: any) => {
                toast.update(id, { render: "Configuration saved successfully", type: "success", isLoading: false, autoClose: 5000 });
            })
            .catch((error: any) => {
                const errorMessage = error?.response?.data.message ? error?.response?.data.message : error?.response?.data
                toast.update(id, { render: errorMessage, type: "error", isLoading: false, autoClose: 5000 });
            });
    }

    return (
        <>
            {loading && <LoadingOverlay />}
            <div className='row'>
                <div className='col-lg-8 col-sm-12 col-xs-12'>
                    <h2 className='callRecording-h2'>Configuration</h2>
                    <form onSubmit={configForm.handleSubmit(onConfigSubmit)} noValidate>
                        <div className="form-row">
                            <div className="form-group col-md-9 col-sm-9">
                                <label htmlFor="GptTuningStyle">Gpt Tuning Style</label>
                                <Controller
                                    control={configForm.control}
                                    name="GptTuningStyle"
                                    defaultValue={5}
                                    render={({ field: { value, onChange } }) => (
                                        <Box>
                                            <Slider
                                                marks
                                                step={1}
                                                value={value}
                                                //valueLabelDisplay="auto"
                                                min={0}
                                                max={9}
                                                onChange={onChange}
                                            />
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }} style={{ margin: "-5px auto auto auto" }}>
                                                <Typography variant="body2" >
                                                    Casual
                                                </Typography>
                                                <Typography variant="body2" >
                                                    Professional
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )}
                                />
                                {configForm.formState.errors.GptTuningStyle && <small className="form-text  form-error">{configForm.formState.errors.GptTuningStyle.message}</small>}
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group col-md-9 col-sm-9">
                                <label htmlFor="SharePointSiteName">SharePoint SiteName</label>
                                <input type="text" className="form-control"
                                    {...configForm.register('SharePointSiteName', {
                                        required: {
                                            value: true,
                                            message: 'SharePoint SiteName is required'
                                        },
                                        maxLength: {
                                            value: 1000,
                                            message: "Maximum 1000 characters allowed"
                                        }
                                    })}
                                />
                                {configForm.formState.errors.SharePointSiteName && <small className="form-text  form-error">{configForm.formState.errors.SharePointSiteName.message}</small>}
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group col-md-9 col-sm-9">
                                <label htmlFor="CallRecorderSharePointUploadFolder">Call Recorder SharePoint Upload Folder</label>
                                <input type="text" className="form-control"
                                    {...configForm.register('CallRecorderSharePointUploadFolder', {
                                        required: {
                                            value: true,
                                            message: 'Call Recorder SharePoint Upload Folder is required'
                                        },
                                        maxLength: {
                                            value: 1000,
                                            message: "Maximum 1000 characters allowed"
                                        }
                                    })}
                                />
                                {configForm.formState.errors.CallRecorderSharePointUploadFolder && <small className="form-text  form-error">{configForm.formState.errors.CallRecorderSharePointUploadFolder.message}</small>}
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group col-md-9 col-sm-9">
                                <label htmlFor="ProofreadingSharePointFolderName">Sharepoint Proofreading Folder Name</label>
                                <input type="text" className="form-control"
                                    {...configForm.register('ProofreadingSharePointFolderName', {
                                        required: {
                                            value: true,
                                            message: 'Sharepoint Proofreading Folder Name is required'
                                        },
                                        maxLength: {
                                            value: 1000,
                                            message: "Maximum 1000 characters allowed"
                                        }
                                    })}
                                />
                                {configForm.formState.errors.ProofreadingSharePointFolderName && <small className="form-text  form-error">{configForm.formState.errors.ProofreadingSharePointFolderName.message}</small>}
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group col-md-9 col-sm-9">
                                <label htmlFor="SummarizationSharePointFolderName">Sharepoint Summarization Folder Name</label>
                                <input type="text" className="form-control"
                                    {...configForm.register('SummarizationSharePointFolderName', {
                                        required: {
                                            value: true,
                                            message: 'Sharepoint Summarization Folder Name is required'
                                        },
                                        maxLength: {
                                            value: 1000,
                                            message: "Maximum 1000 characters allowed"
                                        }
                                    })}
                                />
                                {configForm.formState.errors.SummarizationSharePointFolderName && <small className="form-text  form-error">{configForm.formState.errors.SummarizationSharePointFolderName.message}</small>}
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group col-md-9 col-sm-9">
                                <button type="submit" className="btn btn-outline-success">Submit</button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </>
    )
}


export default TranscriberConfig